import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { BsFiletypeXls } from "react-icons/bs";
import { CgUnavailable } from "react-icons/cg";
import {
  FaChartPie,
  FaFolderOpen,
  FaHome,
  FaFileInvoice,
  FaSearch,
} from "react-icons/fa";
import { HiOutlineChevronLeft } from "react-icons/hi";
import { HiChevronRight } from "react-icons/hi2";
import Swal from "sweetalert2";

const API_URL = process.env.REACT_APP_API_URL;

function Unit_sqyd_added() {
  const [projects, setProjects] = useState([]);
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [formData, setFormData] = useState({
    project_id: "",
    plot_id: "",
    start_date: "",
    end_date: "",
  });
  const [filteredPlots, setFilteredPlots] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const getAuthToken = () => localStorage.getItem("token");

  useEffect(() => {
    const fetchProjects = async () => {
      const token = getAuthToken();
      if (!token) return;

      try {
        const projectsResponse = await fetch(`${API_URL}/project-list-block`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const projectsData = await projectsResponse.json();
        if (projectsData.status) {
          setProjects(projectsData.data || []);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        setError("Failed to load projects");
        Swal.fire("Error", "Failed to load projects", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchPlots = async () => {
      if (!formData.project_id) {
        setFilteredPlots([]);
        setPlots([]);
        return;
      }
      const token = getAuthToken();
      if (!token) return;

      setLoading(true);
      try {
        const plotsResponse = await fetch(
          `${API_URL}/all-plot-list-by-id?project_id=${formData.project_id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const plotsData = await plotsResponse.json();
        if (plotsData.status) {
          const allPlots = plotsData.data || [];
          setPlots(allPlots);
          setFilteredPlots(allPlots);
        } else {
          setPlots([]);
          setFilteredPlots([]);
        }
      } catch (err) {
        console.error("Error fetching plots:", err);
        setError("Failed to load units");
        Swal.fire("Error", "Failed to load units", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchPlots();
  }, [formData.project_id]);

  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      project_id: projectId,
      plot_id: "",
    }));
    setLedgerData(null);
  };

  const handlePlotChange = (e) => {
    const plotId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      plot_id: plotId,
    }));
  };

  const fetchLedgerData = async (page = 1) => {
    if (!formData.project_id || !formData.plot_id) {
      Swal.fire("Warning", "Please select both project and unit", "warning");
      return;
    }

    setLoading(true);
    setError(null);
    const token = getAuthToken();

    try {
      const params = new URLSearchParams({
        project_id: formData.project_id,
        plot_id: formData.plot_id,
        page: page,
        limit: 10,
      });

      if (formData.start_date) params.append("start_date", formData.start_date);
      if (formData.end_date) params.append("end_date", formData.end_date);
      if (searchQuery) params.append("search", searchQuery);

      const url = `${API_URL}/plot-area-ledger-report?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized: Please log in again.");
        }
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch ledger data.");
      }

      const data = await response.json();

      if (data.success === "1") {
        const transformedData = transformAPIResponse(data.data);
        setLedgerData(transformedData);
        setTotalPages(data.data.totalPages || 1);
        setCurrentPage(page);
      } else {
        throw new Error(data.message || "Failed to load ledger data");
      }
    } catch (err) {
      console.error("Fetch ledger data error:", err);
      setError(err.message);
      Swal.fire("Error", err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const transformAPIResponse = (apiData) => {
    if (!apiData || !apiData.users_with_area) {
      return apiData;
    }

    const hierarchy = apiData.users_with_area.map((user) => ({
      name: user.name,
      mobile: user.mobile,
      buysqrt: user.buysqrt,
      area_distributed: user.total_area_distributed,
      role: user.role,
      hierarchy_levels: user.hierarchy_levels,
    }));

    return {
      ...apiData,
      area_distribution: [
        {
          hierarchy: hierarchy,
        },
      ],
    };
  };

  const handleExportExcel = () => {
    if (!ledgerData) return;

    const headers = [
      "SR. No.",
      "Project",
      "Unit",
      "Associates Name",
      "Associates Mobile",
      "Old Sqyd",
      "New Sqyd",
      "Total Sqyd",
    ];

    const rows =
      ledgerData.area_distribution?.[0]?.hierarchy?.map((entry, index) => {
        const oldSqyd =
          parseFloat(entry.buysqrt || 0) -
          parseFloat(entry.area_distributed || 0);

        return [
          index + 1,
          ledgerData.project?.name || "-",
          ledgerData.plot?.plot_shop_villa_no || "-",
          entry.name || "-",
          entry.mobile || "-",
          oldSqyd > 0 ? oldSqyd.toFixed(2) : "0.00",
          entry.area_distributed || "0.00",
          entry.buysqrt || "0.00",
        ];
      }) || [];

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

    const date = new Date().toISOString().split("T")[0];
    a.download = `unit_sqyd_report_${
      ledgerData.project?.name || "project"
    }_${ledgerData.plot?.plot_shop_villa_no || "unit"}_${date}.csv`;

    a.href = url;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      fetchLedgerData(pageNumber);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  if (loading && projects.length === 0) {
    return (
      <div
        style={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
        }}
      >
        <div
          style={{
            width: "50px",
            height: "50px",
            border: "4px solid #e2e8f0",
            borderTop: "4px solid #1e5799",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { from {transform:rotate(0deg);} to {transform:rotate(360deg);} }`}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
        padding: "24px",
        fontFamily: "'Poppins', 'DM Sans', sans-serif",
      }}
    >
      <div style={{ maxWidth: "1500px", margin: "0 auto" }}>
        <div
          style={{
            background: "#ffffff",
            borderRadius: "20px",
            boxShadow: "0 12px 40px rgba(30, 87, 153, 0.1)",
            border: "1.5px solid #e2e8f0",
            overflow: "hidden",
          }}
        >
          {/* Gradient Bar */}
          <div
            style={{
              height: "5px",
              background: "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
            }}
          />

          {/* 🎯 HEADER */}
          <div
            style={{
              padding: "24px 30px",
              background: "linear-gradient(135deg, #ffffff, #f8fafc)",
              borderBottom: "1.5px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "14px",
                  background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  fontSize: "22px",
                  boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
                  flexShrink: 0,
                }}
              >
                <FaChartPie />
              </div>
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "22px",
                    fontWeight: "800",
                    color: "#1e3a5f",
                    letterSpacing: "-0.3px",
                  }}
                >
                  Unit Sqyd Added Report
                </h2>
                <p
                  style={{
                    margin: "2px 0 0",
                    fontSize: "12.5px",
                    color: "#64748b",
                    fontWeight: "500",
                  }}
                >
                  View unit area distribution to all associates
                </p>
              </div>
            </div>

            <button
              onClick={handleExportExcel}
              disabled={!ledgerData}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 20px",
                borderRadius: "10px",
                border: "none",
                background: ledgerData
                  ? "linear-gradient(135deg, #059669, #047857)"
                  : "#cbd5e1",
                color: "#ffffff",
                fontSize: "13px",
                fontWeight: "700",
                cursor: ledgerData ? "pointer" : "not-allowed",
                transition: "all 0.25s ease",
                boxShadow: ledgerData
                  ? "0 6px 18px rgba(5, 150, 105, 0.3)"
                  : "none",
                opacity: ledgerData ? 1 : 0.6,
              }}
            >
              <BsFiletypeXls style={{ fontSize: "16px" }} />
              Export CSV
            </button>
          </div>

          {/* 🎯 BODY */}
          <div style={{ padding: "24px 30px" }}>
            {/* Filters */}
            <div
              style={{
                background: "#f8fafc",
                borderRadius: "14px",
                padding: "18px",
                marginBottom: "24px",
                border: "1.5px solid #e2e8f0",
              }}
            >
              <Row className="gy-3">
                <Col md={5}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12.5px",
                      fontWeight: "700",
                      color: "#1e3a5f",
                      marginBottom: "6px",
                    }}
                  >
                    Project <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <span
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#1e5799",
                        fontSize: "14px",
                        pointerEvents: "none",
                      }}
                    >
                      <FaFolderOpen />
                    </span>
                    <select
                      value={formData.project_id}
                      onChange={handleProjectChange}
                      style={{
                        width: "100%",
                        padding: "11px 14px 11px 38px",
                        borderRadius: "10px",
                        border: "1.5px solid #e2e8f0",
                        background: "#ffffff",
                        fontSize: "13.5px",
                        fontWeight: "500",
                        color: "#1e3a5f",
                        cursor: "pointer",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    >
                      <option value="">Select Project</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>

                <Col md={5}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12.5px",
                      fontWeight: "700",
                      color: "#1e3a5f",
                      marginBottom: "6px",
                    }}
                  >
                    Unit <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <span
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#1e5799",
                        fontSize: "14px",
                        pointerEvents: "none",
                      }}
                    >
                      <FaHome />
                    </span>
                    <select
                      value={formData.plot_id}
                      onChange={handlePlotChange}
                      disabled={!formData.project_id}
                      style={{
                        width: "100%",
                        padding: "11px 14px 11px 38px",
                        borderRadius: "10px",
                        border: "1.5px solid #e2e8f0",
                        background: formData.project_id
                          ? "#ffffff"
                          : "#f1f5f9",
                        fontSize: "13.5px",
                        fontWeight: "500",
                        color: "#1e3a5f",
                        cursor: formData.project_id
                          ? "pointer"
                          : "not-allowed",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    >
                      <option value="">Select Unit</option>
                      {filteredPlots.map((plot) => (
                        <option key={plot.id} value={plot.id}>
                          {plot.plot_shop_villa_no} - {plot.colony_name}
                          {plot.total_area ? ` (${plot.total_area} sq.yd)` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>

                <Col md={2} className="d-flex align-items-end">
                  <button
                    onClick={() => {
                      setCurrentPage(1);
                      fetchLedgerData();
                    }}
                    disabled={
                      !formData.project_id || !formData.plot_id || loading
                    }
                    style={{
                      width: "100%",
                      padding: "11px 20px",
                      borderRadius: "10px",
                      border: "none",
                      background:
                        formData.project_id && formData.plot_id
                          ? "linear-gradient(135deg, #1e5799, #2b6cb0)"
                          : "#cbd5e1",
                      color: "#ffffff",
                      fontSize: "13.5px",
                      fontWeight: "700",
                      cursor:
                        formData.project_id && formData.plot_id
                          ? "pointer"
                          : "not-allowed",
                      transition: "all 0.25s ease",
                      boxShadow:
                        formData.project_id && formData.plot_id
                          ? "0 6px 18px rgba(30, 87, 153, 0.3)"
                          : "none",
                      opacity:
                        formData.project_id && formData.plot_id ? 1 : 0.6,
                    }}
                  >
                    {loading ? "Loading..." : "View Details"}
                  </button>
                </Col>
              </Row>
            </div>

            {/* Loading */}
            {loading && (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    border: "4px solid #e2e8f0",
                    borderTop: "4px solid #1e5799",
                    borderRadius: "50%",
                    margin: "0 auto 16px",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                <p
                  style={{
                    color: "#64748b",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  Loading ledger data...
                </p>
              </div>
            )}

            {/* Table */}
            {!loading && ledgerData && (
              <>
                {/* Info Header */}
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #1e5799, #2b6cb0)",
                    borderRadius: "14px",
                    padding: "16px 22px",
                    marginBottom: "20px",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    boxShadow: "0 8px 24px rgba(30, 87, 153, 0.25)",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "12px",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "20px",
                      flexShrink: 0,
                    }}
                  >
                    🏢
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "24px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "11px",
                          opacity: 0.85,
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                        }}
                      >
                        Project
                      </p>
                      <h5
                        style={{
                          margin: "2px 0 0",
                          fontSize: "15px",
                          fontWeight: "800",
                        }}
                      >
                        {ledgerData.project?.name || "-"}
                      </h5>
                    </div>
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "11px",
                          opacity: 0.85,
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                        }}
                      >
                        Unit
                      </p>
                      <h5
                        style={{
                          margin: "2px 0 0",
                          fontSize: "15px",
                          fontWeight: "800",
                        }}
                      >
                        {ledgerData.plot?.plot_shop_villa_no || "-"}
                      </h5>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div
                  style={{
                    overflowX: "auto",
                    borderRadius: "14px",
                    border: "1.5px solid #e2e8f0",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "13px",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background:
                            "linear-gradient(135deg, #1e5799, #2b6cb0)",
                        }}
                      >
                        {[
                          "SR. No.",
                          "Date",
                          "Project",
                          "Unit",
                          "Associate Name",
                          "Mobile",
                          "Old Sqyd",
                          "New Sqyd",
                        ].map((th, i) => (
                          <th
                            key={i}
                            style={{
                              padding: "14px 12px",
                              color: "#ffffff",
                              fontWeight: "700",
                              fontSize: "11.5px",
                              textAlign: "left",
                              letterSpacing: "0.5px",
                              textTransform: "uppercase",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {th}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerData?.users_with_area?.length > 0 ? (
                        (() => {
                          let globalSerialNumber = 1;
                          return ledgerData.users_with_area.flatMap((user) => {
                            const rows = [];

                            if (user.plot_entries?.length > 0) {
                              user.plot_entries.forEach((entry) => {
                                rows.push(
                                  <tr
                                    key={`plot-${user.user_id}-${entry.entry_id}`}
                                    style={{
                                      borderBottom: "1px solid #f1f5f9",
                                      transition: "background 0.2s ease",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.background =
                                        "#f8fafc")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.background =
                                        "transparent")
                                    }
                                  >
                                    <td
                                      style={{
                                        padding: "12px",
                                        fontWeight: "700",
                                        color: "#1e5799",
                                      }}
                                    >
                                      {globalSerialNumber++}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        color: "#64748b",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {formatDate(entry.status_date)}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        fontWeight: "600",
                                        color: "#1e3a5f",
                                      }}
                                    >
                                      {ledgerData.project?.name || "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        color: "#334155",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {ledgerData.plot?.plot_shop_villa_no ||
                                        "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        fontWeight: "600",
                                        color: "#1e3a5f",
                                      }}
                                    >
                                      {user.name || "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        color: "#64748b",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {user.mobile || "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        fontWeight: "700",
                                        color: "#dc2626",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {entry.old_sqyd || "0.00"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        fontWeight: "700",
                                        color: "#059669",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {entry.new_sqyd || "0.00"}
                                    </td>
                                  </tr>
                                );
                              });
                            }

                            if (user.commissions?.length > 0) {
                              user.commissions.forEach((commission) => {
                                rows.push(
                                  <tr
                                    key={`commission-${user.user_id}-${commission.commission_id}`}
                                    style={{
                                      borderBottom: "1px solid #f1f5f9",
                                      transition: "background 0.2s ease",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.background =
                                        "#f8fafc")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.background =
                                        "transparent")
                                    }
                                  >
                                    <td
                                      style={{
                                        padding: "12px",
                                        fontWeight: "700",
                                        color: "#1e5799",
                                      }}
                                    >
                                      {globalSerialNumber++}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        color: "#64748b",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {formatDate(commission.status_date)}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        fontWeight: "600",
                                        color: "#1e3a5f",
                                      }}
                                    >
                                      {ledgerData.project?.name || "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        color: "#334155",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {ledgerData.plot?.plot_shop_villa_no ||
                                        "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        fontWeight: "600",
                                        color: "#1e3a5f",
                                      }}
                                    >
                                      {commission.associate_name ||
                                        user.name ||
                                        "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        color: "#64748b",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {commission.associate_mobile ||
                                        user.mobile ||
                                        "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        fontWeight: "700",
                                        color: "#dc2626",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {commission.old_sqyd || "0.00"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        fontWeight: "700",
                                        color: "#059669",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {commission.new_sqyd || "0.00"}
                                    </td>
                                  </tr>
                                );
                              });
                            }

                            return rows;
                          });
                        })()
                      ) : (
                        <tr>
                          <td
                            colSpan="8"
                            style={{
                              padding: "50px",
                              textAlign: "center",
                              color: "#94a3b8",
                              fontWeight: "600",
                            }}
                          >
                            📭 No data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "20px",
                      gap: "6px",
                    }}
                  >
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "10px",
                        border: "1.5px solid #e2e8f0",
                        background: "#ffffff",
                        color: currentPage === 1 ? "#cbd5e1" : "#1e5799",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor:
                          currentPage === 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      <HiOutlineChevronLeft />
                    </button>

                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index}
                        onClick={() => handlePageChange(index + 1)}
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "10px",
                          border:
                            index + 1 === currentPage
                              ? "none"
                              : "1.5px solid #e2e8f0",
                          background:
                            index + 1 === currentPage
                              ? "linear-gradient(135deg, #1e5799, #2b6cb0)"
                              : "#ffffff",
                          color:
                            index + 1 === currentPage ? "#ffffff" : "#334155",
                          fontWeight: "700",
                          fontSize: "13px",
                          cursor: "pointer",
                          boxShadow:
                            index + 1 === currentPage
                              ? "0 6px 16px rgba(30, 87, 153, 0.3)"
                              : "none",
                        }}
                      >
                        {index + 1}
                      </button>
                    ))}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      style={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "10px",
                        border: "1.5px solid #e2e8f0",
                        background: "#ffffff",
                        color:
                          currentPage === totalPages ? "#cbd5e1" : "#1e5799",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor:
                          currentPage === totalPages
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      <HiChevronRight />
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Empty State */}
            {!loading && !ledgerData && (
              <div style={{ textAlign: "center", padding: "70px 20px" }}>
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "20px",
                    background: "#e8f1fb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                    fontSize: "36px",
                    color: "#1e5799",
                  }}
                >
                  <FaFileInvoice />
                </div>
                <h4
                  style={{
                    color: "#1e3a5f",
                    fontWeight: "800",
                    marginBottom: "8px",
                  }}
                >
                  No Data Available
                </h4>
                <p
                  style={{ color: "#64748b", fontSize: "13.5px", margin: 0 }}
                >
                  Please select a project and unit to view the Unit SQYD Added
                  report
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default Unit_sqyd_added;